/**
 * RingCentral API client for Gluon.
 * Uses oracleServiceAxios so auth headers and base URL are inherited from the CRM service setup.
 * All paths are prefixed with /ringcentral (oracleServiceAxios.baseURL is already /v1).
 */

import type { RingCentralAccount, RingCentralPhoneNumber, RingCentralRecording } from '@numeo/types'
import { oracleServiceAxios } from 'setup/SetupAxios'

// ─── DataSource boundary types (mirrors backend SourceBatch + state shapes) ──

export interface RingCentralSourceBatch {
    sourceType: 'ringcentral'
    sourceIds: string[]
    text: string
    hints?: {
        contacts?: Array<{ sourceId: string; phone?: string; name?: string }>
        attributions?: Array<{ sourceId: string; system: string; externalId: string; nameHint: string }>
    }
}

export type RecordingStatus = 'discovered' | 'transcribing' | 'transcribed' | 'diarizing' | 'ready' | 'pending' | 'processed' | 'unattributed' | 'failed'

interface RingCentralCallLogRow {
    id: string
    sessionId: string
    startTime: string
    duration: number
    direction: 'Inbound' | 'Outbound'
    from: { name?: string; phoneNumber?: string }
    to: { name?: string; phoneNumber?: string }
    recording?: { id: string; contentUri: string; type: string }
    action?: string
    result?: string
    recordingPresent: boolean
    localRecordingId?: string
    localStatus?: RecordingStatus
}

export interface RingCentralAccountState {
    workspace_id: string
    account_id?: string
    last_poll_cursor?: string | null
    last_poll_at?: string | { _seconds: number; _nanoseconds: number }
}

export type RingCentralStatusCounts = Record<RecordingStatus, number>

export interface RingCentralAccountStats {
    counts: RingCentralStatusCounts
    /** ISO; oldest started_at among recordings with status === 'pending'. */
    oldestPendingAt: string | null
    /** ISO; newest started_at among recordings in a terminal state. */
    lastProcessedAt: string | null
    /** ISO; newest started_at across every recording for this account. */
    lastIngestedAt: string | null
}

const rc = oracleServiceAxios

// OAuth
export async function initiateOAuth(workspaceId: string) {
    const response = await rc.post('/ringcentral/oauth/authorize', null, { params: { workspaceId } })
    return response.data
}

async function disconnectAccount(workspaceId: string, accountId: string) {
    return rc.post('/ringcentral/oauth/disconnect', { workspaceId, accountId })
}

// Accounts
export async function listAccounts(workspaceId: string): Promise<RingCentralAccount[]> {
    const response = await rc.get('/ringcentral/accounts', { params: { workspaceId } })
    return response.data.data || []
}

async function updateAccount(workspaceId: string, accountId: string, data: { account_name?: string }) {
    return rc.patch(`/ringcentral/accounts/${accountId}`, { workspaceId, ...data })
}

export async function deleteAccount(workspaceId: string, accountId: string) {
    return rc.delete(`/ringcentral/accounts/${accountId}`, { params: { workspaceId } })
}

async function syncPhoneNumbers(workspaceId: string, accountId: string) {
    return rc.post(`/ringcentral/accounts/${accountId}/sync-phone-numbers`, { workspaceId })
}

// Phone Numbers
async function listPhoneNumbers(accountId: string): Promise<RingCentralPhoneNumber[]> {
    const response = await rc.get('/ringcentral/phone-numbers', { params: { accountId } })
    return response.data.data || []
}

async function updatePhoneNumber(accountId: string, phoneNumberId: string, data: { label?: string; direction?: string; status?: string }) {
    return rc.patch(`/ringcentral/phone-numbers/${phoneNumberId}`, { accountId, ...data })
}

async function assignAgent(accountId: string, phoneNumberId: string, userId: string) {
    return rc.post(`/ringcentral/phone-numbers/${phoneNumberId}/assign-agent`, { accountId, userId })
}

async function unassignAgent(accountId: string, phoneNumberId: string, userId: string) {
    return rc.delete(`/ringcentral/phone-numbers/${phoneNumberId}/assign-agent`, { params: { accountId, userId } })
}

// Recordings
export async function fetchRecordings(workspaceId: string, accountId: string, dateFrom?: string, dateTo?: string) {
    return rc.post('/ringcentral/recordings/fetch', { workspaceId, accountId, dateFrom, dateTo })
}

export async function listRecordings(accountId: string, status?: string, limit?: number): Promise<RingCentralRecording[]> {
    const response = await rc.get('/ringcentral/recordings', { params: { accountId, status, limit } })
    return response.data.data || []
}

async function getRecording(recordingId: string): Promise<RingCentralRecording> {
    const response = await rc.get(`/ringcentral/recordings/${recordingId}`)
    return response.data.data
}

/**
 * Fetch audio from the RC proxy endpoint and return a blob: URL for the
 * <audio> element. Using axios ensures auth headers are sent; the blob URL
 * is valid for the lifetime of the page.
 */
/** Read-only preview of the SourceBatch this recording would emit. Returns null if not yet processable. */
export async function getRecordingBatch(recordingId: string): Promise<RingCentralSourceBatch | null> {
    const response = await rc.get(`/ringcentral/recordings/${recordingId}/batch`)
    return response.data.data.batch
}

export async function getAudioUrl(recordingId: string, workspaceId: string): Promise<string> {
    const response = await rc.get(`/ringcentral/recordings/${recordingId}/audio`, {
        params: { workspaceId },
        responseType: 'blob',
    })
    return URL.createObjectURL(response.data as Blob)
}

async function updateRecordingStatus(recordingId: string, status: string, sourceId?: string) {
    return rc.patch(`/ringcentral/recordings/${recordingId}`, { status, source_id: sourceId })
}

async function transcribeRecording(recordingId: string) {
    return rc.post(`/ringcentral/recordings/${recordingId}/transcribe`)
}

/**
 * Drive a recording up to the DataSource boundary (download → STT → diarize →
 * 'pending') and return the SourceBatch the DataSource would emit for it.
 * Does NOT run the CRM Process pipeline.
 */
export async function processRecording(
    recordingId: string,
    opts?: { force?: boolean }
): Promise<{ status: RecordingStatus; batch: RingCentralSourceBatch | null }> {
    const response = await rc.post(`/ringcentral/recordings/${recordingId}/process`, null, {
        params: opts?.force ? { force: 'true' } : undefined,
    })
    return response.data.data
}

/** Reset a recording to 'pending' so the next pipeline run picks it up. */
export async function requeueRecording(recordingId: string): Promise<RingCentralRecording> {
    const response = await rc.put(`/ringcentral/recordings/${recordingId}/requeue`)
    return response.data.data
}

/** Permanently delete a recording (Firestore doc + GCS audio). */
export async function deleteRecording(recordingId: string): Promise<void> {
    await rc.delete(`/ringcentral/recordings/${recordingId}`)
}

/**
 * Dry-run preview: returns the SourceBatch[] the next pipeline run would emit,
 * without advancing the cursor or writing status.
 */
export async function previewBatch(limit: number = 20): Promise<RingCentralSourceBatch[]> {
    const response = await rc.post('/ringcentral/recordings/preview', { limit })
    return response.data.data.batches as RingCentralSourceBatch[]
}

/**
 * Live read-through to RingCentral's call-log API for one account, enriched
 * with recordingPresent / localStatus from the local recordings collection.
 */
async function listCallLogs(
    workspaceId: string,
    accountId: string,
    params?: { dateFrom?: string; dateTo?: string; perPage?: number; pageToken?: string }
): Promise<{ records: RingCentralCallLogRow[]; nextPageToken?: string }> {
    const response = await rc.get(`/ringcentral/accounts/${accountId}/call-logs`, {
        params: { workspaceId, ...params },
    })
    return response.data.data
}

/** Polling cursor + last_poll_at for one account. */
export async function getAccountState(workspaceId: string, accountId: string): Promise<RingCentralAccountState | null> {
    const response = await rc.get(`/ringcentral/accounts/${accountId}/state`, { params: { workspaceId } })
    return response.data.data
}

/** Counts per status + cursor timestamps for one account. */
export async function getAccountStats(accountId: string): Promise<RingCentralAccountStats> {
    const response = await rc.get(`/ringcentral/accounts/${accountId}/stats`)
    return response.data.data
}

// Admin
async function pollAllAccounts(workspaceId: string) {
    return rc.post('/ringcentral/admin/poll-all-accounts', null, { params: { workspaceId } })
}

async function forceReady(recordingId: string) {
    return rc.patch(`/ringcentral/admin/recordings/${recordingId}/force-ready`)
}

async function deleteGCSAudio(recordingId: string) {
    return rc.delete(`/ringcentral/admin/recordings/${recordingId}/delete-gcs-audio`)
}
